import { useEffect, useRef } from 'react';
import { LONG_PRESS_MS, MOVE_SLOP, SWIPE_DISTANCE, SWIPE_FOLLOW } from './touchRules';
import { findTouchTipTarget } from './touchTips';
/**
 * Touch gestures for an item: swipe left (its actions) and press and hold. The numbers are the
 * shared touch rules (`touchRules`). Vertical scrolling is left alone. The item slides a little
 * with the finger while swiping. After a swipe or a long press the next click is swallowed, so
 * the gesture does not also open the item.
 *
 * Holding an icon-only control inside the item shows the control's label instead of the
 * item's long press.
 *
 * @example
 * ```tsx
 * const gestures = usePressGesture({ onSwipeLeft: openActions, onLongPress: startSelecting });
 * <div {...gestures.handlers} onClick={(e) => { if (gestures.consumeClick()) return; open(); }} />
 * ```
 * @export
 */
const usePressGesture = ({ onSwipeLeft, onLongPress }) => {
    const start = useRef(null);
    const timer = useRef();
    const swallowClick = useRef(false);
    const latest = useRef({ onSwipeLeft, onLongPress });
    latest.current = { onSwipeLeft, onLongPress };
    useEffect(() => () => clearTimeout(timer.current), []);
    const reset = (el) => {
        clearTimeout(timer.current);
        start.current = null;
        el.style.transition = 'transform 150ms ease-out';
        el.style.transform = '';
    };
    const enabled = !!onSwipeLeft || !!onLongPress;
    const handlers = enabled ? {
        onTouchStart: (e) => {
            if (e.touches.length !== 1)
                return;
            start.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            swallowClick.current = false;
            clearTimeout(timer.current);
            const tip = findTouchTipTarget(e.target);
            const onLabelledControl = !!tip && tip !== e.currentTarget && e.currentTarget.contains(tip);
            if (latest.current.onLongPress && !onLabelledControl) {
                timer.current = setTimeout(() => {
                    var _a, _b;
                    start.current = null;
                    swallowClick.current = true;
                    (_b = (_a = latest.current).onLongPress) === null || _b === void 0 ? void 0 : _b.call(_a);
                }, LONG_PRESS_MS);
            }
        },
        onTouchMove: (e) => {
            if (!start.current)
                return;
            const dx = e.touches[0].clientX - start.current.x;
            const dy = e.touches[0].clientY - start.current.y;
            if (Math.abs(dx) > MOVE_SLOP || Math.abs(dy) > MOVE_SLOP)
                clearTimeout(timer.current);
            if (latest.current.onSwipeLeft && dx < 0 && Math.abs(dx) > Math.abs(dy)) {
                e.currentTarget.style.transition = 'none';
                e.currentTarget.style.transform = `translateX(${Math.max(dx, -SWIPE_FOLLOW)}px)`;
            }
        },
        onTouchEnd: (e) => {
            const from = start.current;
            reset(e.currentTarget);
            if (!from)
                return;
            const t = e.changedTouches[0];
            const dx = t.clientX - from.x;
            const dy = t.clientY - from.y;
            if (latest.current.onSwipeLeft && dx < -SWIPE_DISTANCE && Math.abs(dx) > Math.abs(dy) * 1.5) {
                swallowClick.current = true;
                latest.current.onSwipeLeft();
            }
        },
        onTouchCancel: (e) => reset(e.currentTarget),
        // Stop the browser's own long-press menu on the item
        onContextMenu: (e) => { if (latest.current.onLongPress)
            e.preventDefault(); },
    } : {};
    /** True once after a swipe or long press: the click that follows should be ignored */
    const consumeClick = () => {
        const swallow = swallowClick.current;
        swallowClick.current = false;
        return swallow;
    };
    return { handlers, consumeClick };
};
export default usePressGesture;
